import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useStudioWorkspace } from "../context/StudioWorkspaceProvider";
import { activateRuntimeConnection, deactivateRuntimeConnection } from "./runtimeConnectionService";
import {
  getRuntimeContextSnapshot,
  getRuntimeVersion,
  subscribeRuntimeContext,
} from "./runtimeDb";

const StudioRuntimeContext = createContext(null);

const IDLE = "idle";
const CONNECTING = "connecting";
const CONNECTED = "connected";
const ERROR = "error";

/**
 * StudioRuntimeProvider — single shared RuntimeContext for all Studio modules.
 *
 * When the active workspace connection changes it disposes the previous runtime,
 * initializes the new one, updates the global runtime binding, and notifies
 * every subscriber. Explorer, Query Engine, AI, Agents, Workflows, Health
 * Check, Deep Test, and Firestore Manager all read from this context.
 */
export function StudioRuntimeProvider({ children }) {
  const { workspace } = useStudioWorkspace();
  const connectionId = workspace.connectionId || "";

  const [status, setStatus] = useState(connectionId ? CONNECTING : IDLE);
  const [loading, setLoading] = useState(Boolean(connectionId));
  const [error, setError] = useState("");
  const [instance, setInstance] = useState(null);
  const [connection, setConnection] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [runtimeSnapshot, setRuntimeSnapshot] = useState(getRuntimeContextSnapshot);

  useEffect(() => subscribeRuntimeContext(setRuntimeSnapshot), []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!connectionId) {
        await deactivateRuntimeConnection(null);
        if (cancelled) return;
        setInstance(null);
        setConnection(null);
        setStatus(IDLE);
        setLoading(false);
        setError("");
        return;
      }
      setLoading(true);
      setStatus(CONNECTING);
      setError("");
      try {
        const { connection: conn, instance: inst } = await activateRuntimeConnection(connectionId);
        if (cancelled) return;
        setConnection(conn);
        setInstance(inst);
        setStatus(CONNECTED);
      } catch (err) {
        if (cancelled) return;
        setInstance(null);
        setStatus(ERROR);
        setError(err?.message || "Failed to initialize connection runtime");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
  }, [connectionId, reloadToken]);

  const reconnect = useCallback(() => setReloadToken((n) => n + 1), []);

  const disconnect = useCallback(async () => {
    await deactivateRuntimeConnection(connectionId);
    setInstance(null);
    setConnection(null);
    setStatus(IDLE);
  }, [connectionId]);

  const isRuntimeActive = status === CONNECTED && Boolean(instance);

  const value = useMemo(() => ({
    // Canonical RuntimeContext fields
    runtimeApp: instance?.app || runtimeSnapshot.runtimeApp || null,
    runtimeDb: isRuntimeActive ? (instance?.firestore || runtimeSnapshot.runtimeDb) : null,
    runtimeFirestore: isRuntimeActive ? (instance?.firestore || runtimeSnapshot.runtimeDb) : null,
    runtimeAuth: instance?.auth || runtimeSnapshot.runtimeAuth || null,
    runtimeStorage: instance?.storage || null,
    runtimeFunctions: instance?.functions || null,
    runtimeConnectionId: connectionId || null,
    runtimeProjectId:
      connection?.firebaseProjectId ||
      connection?.projectId ||
      instance?.app?.options?.projectId ||
      runtimeSnapshot.runtimeProjectId ||
      null,
    runtimeConnection: connection,
    runtimeStatus: status,
    isRuntimeActive,
    lastRuntimeRefresh: runtimeSnapshot.lastRuntimeRefresh,
    runtimeVersion: runtimeSnapshot.runtimeVersion || getRuntimeVersion(),
    loading,
    error,
    reconnect,
    disconnect,
  }), [
    instance,
    connection,
    connectionId,
    status,
    isRuntimeActive,
    runtimeSnapshot,
    loading,
    error,
    reconnect,
    disconnect,
  ]);

  return <StudioRuntimeContext.Provider value={value}>{children}</StudioRuntimeContext.Provider>;
}

export function useStudioRuntime() {
  const ctx = useContext(StudioRuntimeContext);
  if (!ctx) throw new Error("useStudioRuntime must be used within StudioRuntimeProvider");
  return ctx;
}

/** Alias — canonical RuntimeContext hook. */
export function useRuntimeContext() {
  return useStudioRuntime();
}

/**
 * useRuntimeConnection() — convenience hook returning the active runtime
 * services for the selected connection.
 */
export function useRuntimeConnection() {
  const {
    runtimeDb,
    runtimeFirestore,
    runtimeApp,
    runtimeAuth,
    runtimeStorage,
    runtimeFunctions,
    runtimeConnection,
    runtimeConnectionId,
    runtimeProjectId,
    runtimeStatus,
    isRuntimeActive,
    lastRuntimeRefresh,
    runtimeVersion,
    loading,
    error,
    reconnect,
    disconnect,
  } = useStudioRuntime();

  return {
    app: runtimeApp,
    firestore: runtimeFirestore || runtimeDb,
    db: runtimeDb,
    auth: runtimeAuth,
    storage: runtimeStorage,
    functions: runtimeFunctions,
    connection: runtimeConnection,
    connectionId: runtimeConnectionId,
    projectId: runtimeProjectId,
    status: runtimeStatus,
    isActive: isRuntimeActive,
    lastRuntimeRefresh,
    runtimeVersion,
    loading,
    error,
    reconnect,
    disconnect,
  };
}
